import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const FAQSection = styled.section`
  padding: 120px 0;
  background: ${props => props.theme.colors.dark};
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const Title = styled(motion.h2)`
  font-family: ${props => props.theme.fonts.display};
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 24px;
  color: ${props => props.theme.colors.white};
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FAQItem = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.theme.colors.mediumGray}40, ${props => props.theme.colors.darkGray}60);
  border: 1px solid ${props => props.theme.colors.mediumGray};
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;

  ${props => props.isOpen && `
    border-color: ${props.theme.colors.primary}60;
    box-shadow: 0 10px 30px ${props.theme.colors.primary}20;
  `}
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 24px;
  background: none;
  border: none;
  text-align: left;
  color: ${props => props.theme.colors.white};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const FAQIcon = styled(motion.span)`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  transition: all 0.3s ease;
`;

const FAQAnswer = styled(motion.div)`
  padding: 0 24px 24px;
  color: ${props => props.theme.colors.lightGray};
  line-height: 1.6;
  font-size: 1rem;
`;

const FAQ = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [openItems, setOpenItems] = useState(new Set([0])); // Primeiro item aberto por padrão

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqs = [
    {
      question: "Como funciona o CCP - Consignação com Propósito?",
      answer: "O CCP é 100% online com módulos completos em vídeo HD. Você aprende o método no seu ritmo e tem acesso vitalício ao conteúdo. Inclui certificado de conclusão e suporte prioritário por 6 meses."
    },
    {
      question: "O que está incluído no curso?",
      answer: "Você recebe: Módulos completos em vídeo HD, suporte prioritário por 6 meses, certificado de conclusão e acesso vitalício a todo o conteúdo. Tudo que você precisa para dominar a consignação de veículos."
    },
    {
      question: "Como funciona a garantia de 7 dias?",
      answer: "Se você não ficar 100% satisfeito com o curso ou não ver resultados práticos em seu negócio, devolvemos todo seu dinheiro sem perguntas. Sua satisfação é nossa prioridade."
    },
    {
      question: "Quanto custa o curso?",
      answer: "Na oferta Black Friday, o curso sai de R$ 1.497 por apenas R$ 497 com o cupom CCPBLACK. É um pagamento único com acesso vitalício. Oferta por tempo limitado!"
    },
    {
      question: "Posso parcelar o pagamento?",
      answer: "Sim! Aceitamos cartão de crédito com parcelamento em até 12x, PIX à vista e boleto bancário. Todas as formas de pagamento são processadas de forma segura pela CursEduca."
    },
    {
      question: "Por quanto tempo tenho acesso ao curso?",
      answer: "Você tem acesso vitalício! Pode assistir quantas vezes quiser, no seu ritmo, sem pressa. O conteúdo fica disponível para sempre na sua conta."
    }
  ];

  return (
    <FAQSection ref={ref}>
      <Container>
        <Header>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            PERGUNTAS FREQUENTES
          </Title>
        </Header>

        <FAQList>
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              isOpen={openItems.has(index)}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <FAQQuestion onClick={() => toggleItem(index)}>
                {faq.question}
                <FAQIcon
                  animate={{ rotate: openItems.has(index) ? 45 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  +
                </FAQIcon>
              </FAQQuestion>
              
              <AnimatePresence>
                {openItems.has(index) && (
                  <FAQAnswer
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {faq.answer}
                  </FAQAnswer>
                )}
              </AnimatePresence>
            </FAQItem>
          ))}
        </FAQList>
      </Container>
    </FAQSection>
  );
};

export default FAQ;